import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Pagination,
  Box,
  Typography,
  Select,
  FormControl,
  Collapse,
  useTheme,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useOrgan } from '../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../layouts/PermanentSidebarMUI'
import { Fragment, useState, useMemo } from 'react'
import { maskCurrency, maskInteger } from '../../utils/maskUtils'
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons'
import {
  getActionButtonStyles,
  getActionIconColor,
} from '../../utils/actionButtonStyles'
import TableSkeleton from './TableSkeleton'
// Função auxiliar para obter valor de um objeto aninhado usando notação de ponto
function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj)
}

export default function GenericTable({
  data = [],
  columns = [],
  actions = [],
  totalPages = 1,
  currentPage = 0,
  onPageChange = () => {},
  rowsPerPage = 25,
  onRowsPerPageChange = () => {},
  rowColorConfig, // Nova prop para configuração de cores
  isLoading = false,
  isFetching = false,
  expandedRowId = null,
  renderExpandedRow,
}) {
  const theme = useTheme()

  const { selectedOrgan } = useOrgan()
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  // estado do menu dropdown de ações
  const [anchorEl, setAnchorEl] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
  const open = Boolean(anchorEl)

  const handleOpenMenu = (event, row) => {
    setAnchorEl(event.currentTarget)
    setActiveRow(row)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setActiveRow(null)
  }

  const handleActionClick = (action) => {
    handleCloseMenu()
    if (action?.onClick && activeRow) {
      action.onClick(activeRow)
    }
  }

  // Função para determinar as cores da linha
  const getRowColors = useMemo(() => {
    return (row) => {
      if (!rowColorConfig) {
        return { backgroundColor: 'inherit', color: 'inherit' }
      }

      const { colorMap, propertyKey } = rowColorConfig
      const propertyValue = getNestedValue(row, propertyKey)

      if (colorMap && propertyValue !== undefined) {
        const colorConfig = colorMap[propertyValue]
        if (colorConfig) {
          return {
            backgroundColor: colorConfig.bgColor,
            color: colorConfig.color,
          }
        }
      }

      return { backgroundColor: 'inherit', color: 'inherit' }
    }
  }, [rowColorConfig])

  // Filtrar ações visíveis para cada linha
  const getVisibleActions = useMemo(() => {
    return (row) => {
      return actions.filter((action) => {
        try {
          return action.show ? action.show(row) : true
        } catch {
          return false
        }
      })
    }
  }, [actions])

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Overlay durante refetch */}
      {isFetching || isLoading ? (
        <TableSkeleton
          rows={rowsPerPage > 10 ? 10 : rowsPerPage}
          columns={columns.length}
          showActions={actions.length > 0}
        />
      ) : (
        <>
          <TableContainer sx={{ overflowY: 'auto' }}>
            <Table stickyHeader aria-label="tabela generica">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || 'left'}
                      sx={{
                        width: column.width || 'auto',
                        minWidth: column.minWidth || 100,
                        backgroundColor: primaryColor,
                        color: '#FFFFFF',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                      }}>
                      {column.label}
                    </TableCell>
                  ))}
                  {actions.length > 0 && (
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: primaryColor,
                        color: '#FFFFFF',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                      }}>
                      Ações
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((row, rowIndex) => {
                    const rowColors = getRowColors(row)
                    const visibleActions = getVisibleActions(row)
                    const hasVisibleActions = visibleActions.length > 0

                    return (
                      <Fragment key={row.id || rowIndex}>
                        <TableRow
                          role="checkbox"
                          tabIndex={-1}
                          sx={{
                            backgroundColor: rowColors.backgroundColor,
                            color: rowColors.color,
                          }}>
                          {columns.map((column) => {
                            const value = row[column.key]
                            return (
                              <Tooltip
                                key={column.key}
                                title={value}
                                placement="bottom-start">
                                <TableCell
                                  align={column.align || 'left'}
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: column.width || 'auto',
                                    minWidth: column.minWidth || 100,
                                    maxWidth: column.maxWidth || 200,
                                    color: rowColors.color,
                                    textTransform: 'uppercase',
                                  }}>
                                  {column.render
                                    ? column.render(value, row)
                                    : column.format && typeof value === 'number'
                                      ? column.format(value)
                                      : column.isMoney
                                        ? maskCurrency(value)
                                        : column.isInteger
                                          ? maskInteger(value)
                                          : value}
                                </TableCell>
                              </Tooltip>
                            )
                          })}

                          {/* Botão de ações - Só mostra se houver ações visíveis */}
                          {actions.length > 0 && hasVisibleActions && (
                            <TableCell
                              align="center"
                              sx={{
                                color: rowColors.color,
                              }}>
                              <IconButton
                                size="small"
                                sx={{
                                  paddingX: 2,
                                  color: rowColors.color,
                                }}
                                onClick={(e) => handleOpenMenu(e, row)}>
                                <FontAwesomeIcon icon={faEllipsisVertical} />
                              </IconButton>
                            </TableCell>
                          )}

                          {/* Célula vazia para manter alinhamento quando não há ações */}
                          {actions.length > 0 && !hasVisibleActions && (
                            <TableCell />
                          )}
                        </TableRow>

                        {renderExpandedRow && (
                          <TableRow>
                            <TableCell
                              colSpan={
                                columns.length + (actions.length > 0 ? 1 : 0)
                              }
                              sx={{
                                paddingBottom: 0,
                                paddingTop: 0,
                                borderBottom: 'none',
                                backgroundColor: '#f8f9fa',
                              }}>
                              <Collapse
                                in={String(expandedRowId) === String(row.id)}
                                timeout="auto"
                                unmountOnExit>
                                {renderExpandedRow(row)}
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      Nenhum dado encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* menu suspenso de ações */}
            {activeRow && (
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                {getVisibleActions(activeRow).map((action, idx) => {
                  // 🎨 LÓGICA DE CORES ATUALIZADA
                  const actionStyles = getActionButtonStyles(
                    action.buttonClass,
                    theme
                  )
                  const iconColor = getActionIconColor(
                    action.buttonClass,
                    theme
                  )

                  return (
                    <MenuItem
                      key={idx}
                      onClick={() => handleActionClick(action)}
                      sx={{
                        // Aplica backgroundColor se existir
                        ...(actionStyles.backgroundColor && {
                          backgroundColor: actionStyles.backgroundColor,
                          '&:hover': {
                            backgroundColor:
                              actionStyles['&:hover']?.backgroundColor ||
                              actionStyles.backgroundColor,
                          },
                        }),
                        // Cor do texto
                        color: actionStyles.color,
                        // Hover effects padrões do MUI
                        '&:hover': {
                          opacity: 0.9,
                        },
                      }}>
                      <FontAwesomeIcon
                        icon={action.icon}
                        style={{
                          marginRight: 8,
                          // Aplica cor no ícone se for customizada
                          color: iconColor,
                        }}
                      />
                      {action.title}
                    </MenuItem>
                  )
                })}
              </Menu>
            )}
          </TableContainer>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 2,
              borderTop: '1px solid rgba(224, 224, 224, 1)',
            }}>
            {/* Seletor de itens por página (esquerda) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Itens por página:</Typography>
              <FormControl size="small">
                <Select
                  value={rowsPerPage}
                  onChange={(event) => onRowsPerPageChange(event)}
                  sx={{ minWidth: 70 }}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Paginação numérica (centro) */}
            <Pagination
              count={totalPages}
              page={currentPage + 1}
              onChange={(event, page) => onPageChange(page - 1)}
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
            />

            {/* Info de registros (direita) - OPCIONAL */}
            <Typography variant="body2" color="text.secondary">
              {currentPage * rowsPerPage + 1}–
              {Math.min(
                (currentPage + 1) * rowsPerPage,
                totalPages * rowsPerPage
              )}{' '}
              de {totalPages * rowsPerPage}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  )
}
